import { InfoIcon } from 'lucide-react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { withTooltip } from '@crossnokaye/ux-common';
import {
  ActionBarActions,
  ActionBarRoot,
  ActionBarSaveButton,
  ActionBarStatus,
} from '@crossnokaye/ux-common/ckds';

import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';
import { Button } from '@/ux-common/components/button/button';

import {
  buildDirtyCellTargets,
  parseDirtyFieldsValues,
} from '../bulk-edit-table/use-dirty-cell-targets';
import type { EquipmentBulkEditFormValues } from '../equipment-bulk-edit.types';

const SaveBarSaveButtonWithTooltip = withTooltip(ActionBarSaveButton);

interface EquipmentBulkEditSaveBarProps {
  isPushOutOpen: boolean;
  isValidating?: boolean;
  onCancel: () => Promise<void> | void;
  onPreview: () => void;
  onReviewSave: () => void;
}

/**
 * EquipmentBulkEditSaveBar
 *
 * Bottom save bar for the bulk editing view. Shows "No unsaved changes" in neutral
 * mode when the form is clean. Hidden when the Bulk Edit push out is open.
 */
export const EquipmentBulkEditSaveBar = ({
  isPushOutOpen,
  isValidating = false,
  onCancel,
  onPreview,
  onReviewSave,
}: EquipmentBulkEditSaveBarProps): React.ReactNode => {
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({ privilege: PRIVILEGES.writeCommands });

  const {
    formState: { dirtyFields },
  } = useFormContext<{ values: EquipmentBulkEditFormValues }>();
  const dirtyFieldCount = buildDirtyCellTargets(parseDirtyFieldsValues(dirtyFields?.values)).length;
  const isDirty = dirtyFieldCount > 0;

  return (
    <ActionBarRoot
      className="relative"
      data-testid="equipment-bulk-edit-save-bar"
      open={!isPushOutOpen && isDirty}
    >
      <ActionBarStatus
        variant="default"
        icon={InfoIcon}
        className={'uppercase text-ckds-base-caution'}
        statusText={`${dirtyFieldCount} cells have unsaved changes`}
      />
      <ActionBarActions>
        <Button className="btn btn-tier3" onClick={onPreview}>
          {'PREVIEW CHANGES'}
        </Button>
        <Button className="btn btn-tier2" onClick={onCancel}>
          {'DISCARD CHANGES'}
        </Button>

        <SaveBarSaveButtonWithTooltip
          type="button"
          disabled={!isDirty || disabledByPermissions || isValidating}
          onClick={onReviewSave}
          tooltipContent={disabledByPermissions ? permissionTooltipText : undefined}
          delayDuration={0}
        >
          {`REVIEW & SAVE ${dirtyFieldCount} CHANGES`}
        </SaveBarSaveButtonWithTooltip>
      </ActionBarActions>
    </ActionBarRoot>
  );
};
